// Invite links — rename (VRG-794). Only the staff-only label changes: the code, and so the URL
// already sent to someone, stays exactly as it is. Same field as "Create tracked link".
import React, { useEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { Button, Input, Modal } from '@repo/ui'

import { useSnackbar } from 'contexts'
import { useTrackedLinkActions, type TrackedLink } from 'hooks/api/useTrackedLinks'
import { getErrorMessage } from 'utils'

import { TRACKED_LINK_LABEL_MAX, labelError } from './labels'


export const RenameTrackedLinkModal = ({ link, onClose }: { link: TrackedLink | null; onClose: () => void }) => {
  const { open: notification } = useSnackbar()
  const { relabel } = useTrackedLinkActions()
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (link) {
      setLabel(link.label)
      setError(null)
    }
  }, [link])

  // §54: Save is live only when there is a real, valid change. Cancel never disables.
  const dirty = Boolean(link) && label.trim() !== link?.label

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!link || !dirty) return
    const err = labelError(label)
    setError(err)
    if (err) return
    relabel.mutate(
      { id: link.id, label: label.trim() },
      {
        onSuccess: () => {
          notification({ type: 'success', title: 'Link renamed' })
          onClose()
        },
        onError: (e) => notification({ type: 'error', title: getErrorMessage(e) }),
      },
    )
  }

  return (
    <Modal
      title="Rename tracked link"
      open={Boolean(link)}
      onClose={onClose}
      description="The new name shows everywhere this link appears, including its past clicks."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => submit()} loading={relabel.isPending} disabled={!dirty}>
            Save
          </Button>
        </>
      }
    >
      <Stack component="form" onSubmit={submit} spacing={3} mt={2}>
        <Input
          label="Label"
          labelAdornment="(staff only)"
          value={label}
          onValueChange={(v: string) => {
            setLabel(v)
            if (error) setError(null)
          }}
          placeholder="Art fair follow-up"
          error={Boolean(error)}
          helperText={error ?? undefined}
          inputProps={{ autoFocus: true, maxLength: TRACKED_LINK_LABEL_MAX, 'data-testid': 'rename-tracked-link-label' } as never}
        />
        {link && (
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5, overflowWrap: 'anywhere' }}>
            The link stays {link.url}, so anyone you already sent it to can still use it.
          </Typography>
        )}
      </Stack>
    </Modal>
  )
}
